// ══════════════════════════════════════════════════════════════════════════
//  La vista de permiso (/oauth/consent): Claude, desde claude.ai, pide usar GEOBAJA
//  con la cuenta de quien entra. Lienzo «vista de permiso», opción A, elegida por el
//  dueño el 2026-10-28 ("Elijo el A"): un modo de la pantalla de entrada, como
//  /restablecer.
//
//  El camino: claude.ai manda a Supabase (Authentication > OAuth Server); Supabase
//  redirige aquí con ?authorization_id=...; la vista lee la solicitud CON LA SESIÓN
//  (sb.auth.solicitudOAuth), dice quién pide y a dónde se vuelve, y manda la decisión
//  (sb.auth.decidirOAuth), que responde la dirección de regreso. Solo un administrador
//  activo da el permiso (el MCP remoto lo vuelve a exigir en cada llamada).
//
//  Motor puro: qué estado se dibuja y con qué palabras. Los textos de esta vista salen
//  de aquí (textoPermiso), como los de entrar salen de textoAcceso.
// ══════════════════════════════════════════════════════════════════════════

export const RUTA_PERMISO = '/oauth/consent';

/** ¿Es la vista de permiso? Tolera la diagonal final. */
export const esRutaPermiso = (pathname) => String(pathname || '').replace(/\/+$/, '') === RUTA_PERMISO;

/** El authorization_id de la dirección (?authorization_id=...), o null. */
export function idDeSolicitud(search) {
  try {
    const id = new URLSearchParams(String(search || '')).get('authorization_id');
    return id && id.trim() ? id.trim() : null;
  } catch (e) {
    return null;
  }
}

/** Solo un administrador activo da el permiso. */
export const esAdministradorActivo = (usuario) => !!usuario && usuario.rol === 'administrador' && usuario.activo !== false;

/** El host de una dirección (claude.ai), o null si no se lee. */
export function hostDe(url) {
  try {
    return new URL(String(url || '')).host || null;
  } catch (e) {
    return null;
  }
}

// Sesión que Auth ya no acepta (vencida o cerrada en otro lado): hay que volver a entrar.
const SIN_SESION = new Set(['bad_jwt', 'session_not_found', 'no_authorization', 'session_expired']);
const codigoDe = (cuerpo) => (cuerpo && typeof cuerpo === 'object' ? (cuerpo.error_code || cuerpo.code || null) : null);
const sinRed = (res) => !res || !!res.errorRed || res.status === 0 || res.status === 429 || res.status >= 500;

/**
 * Lo que dijo Auth de la solicitud.
 * @returns {{tipo:'solicitud', solicitud:Object}|{tipo:'ya_permitida', regreso:string}|{tipo:'sin_sesion'}|{tipo:'vencida'}|{tipo:'sin_lectura'}}
 *   - solicitud: { id, cliente (nombre o null), host (a dónde se vuelve), redirectUri, scope }
 *   - ya_permitida: el permiso ya se había dado; Auth da la dirección de regreso.
 */
export function clasificarSolicitud(res) {
  if (sinRed(res)) return { tipo: 'sin_lectura' };
  if (res.ok) {
    const d = res.datos || {};
    if (d.authorization_id) {
      const cliente = String((d.client && d.client.name) || '').trim() || null;
      return {
        tipo: 'solicitud',
        solicitud: { id: d.authorization_id, cliente, host: hostDe(d.redirect_uri), redirectUri: d.redirect_uri || null, scope: d.scope || '' },
      };
    }
    if (d.redirect_url) return { tipo: 'ya_permitida', regreso: d.redirect_url };
    return { tipo: 'sin_lectura' };
  }
  if (res.status === 401 || SIN_SESION.has(codigoDe(res.cuerpo))) return { tipo: 'sin_sesion' };
  // 400, 403, 404, 410: venció, ya se usó o no existe. Cada solicitud sirve una vez.
  return { tipo: 'vencida' };
}

/**
 * Lo que dijo Auth de la decisión (approve o deny).
 * @returns {{tipo:'hecha', regreso:string}|{tipo:'sin_red'}|{tipo:'sin_sesion'}|{tipo:'vencida'}}
 */
export function clasificarDecision(res) {
  if (res && res.ok && res.datos && res.datos.redirect_url) return { tipo: 'hecha', regreso: res.datos.redirect_url };
  if (sinRed(res)) return { tipo: 'sin_red' };
  if (res.status === 401 || SIN_SESION.has(codigoDe(res.cuerpo))) return { tipo: 'sin_sesion' };
  return { tipo: 'vencida' };
}

/** El estado que se dibuja tras leer la solicitud. */
export function estadoTrasLeer(usuario, lectura) {
  if (!usuario || lectura.tipo === 'sin_sesion') return 'sin_sesion';
  if (lectura.tipo === 'ya_permitida') return 'aprobado';
  if (lectura.tipo === 'solicitud') return esAdministradorActivo(usuario) ? 'permiso' : 'no_admin';
  return lectura.tipo === 'vencida' ? 'vencida' : 'sin_lectura';
}

/** Adonde se vuelve si el regreso automático no ocurre: la dirección de Auth o claude.ai. */
export const regresoManual = (regreso) => (regreso && /^https:\/\//i.test(regreso) ? regreso : 'https://claude.ai');

/**
 * Las palabras de cada estado (la columna del lienzo, opción A).
 * @param {'sin_sesion'|'leyendo'|'permiso'|'no_admin'|'vencida'|'sin_lectura'|'aprobado'|'rechazado'} estado
 * @param {{cliente?:string|null, host?:string|null}} [d]
 * @returns {{titulo:string, linea:null|{icono:string, texto:string, tono?:string}, parrafo:null|string}}
 */
export function textoPermiso(estado, d = {}) {
  const quien = d.cliente || 'Una aplicación';
  const titulo = 'Permiso de acceso';
  switch (estado) {
    case 'sin_sesion': return { titulo: null, linea: null, parrafo: null };
    case 'leyendo': return { titulo, linea: null, parrafo: 'Leyendo la solicitud…' };
    case 'permiso': return {
      titulo,
      linea: null,
      parrafo: `${quien} pide usar GEOBAJA con tu cuenta.`,
      detalle: `${d.cliente || 'La aplicación'} podrá consultar y trabajar en GEOBAJA con tu cuenta y tus permisos de administrador. Lo que haga queda registrado a tu nombre.`,
    };
    case 'no_admin': return {
      titulo,
      linea: { icono: 'lock', texto: `Solo un administrador puede conectar ${d.cliente || 'una aplicación'} con GEOBAJA.` },
      parrafo: `Con esta cuenta ${d.cliente || 'la aplicación'} no recibe acceso. Entra con una cuenta de administrador o vuelve a ${d.cliente || 'la aplicación'}.`,
    };
    case 'vencida': return {
      titulo,
      linea: { icono: 'clock', texto: 'La solicitud venció o ya se usó.' },
      parrafo: 'Vuelve a la aplicación que te trajo aquí y pide conectar de nuevo: cada solicitud sirve una sola vez.',
    };
    case 'sin_lectura': return {
      titulo,
      linea: { icono: 'alert-circle', texto: 'No se pudo leer la solicitud. Revisa tu conexión e inténtalo de nuevo.' },
      parrafo: null,
    };
    case 'aprobado': return { titulo, linea: { icono: 'check', texto: `Diste el permiso a ${d.cliente || 'la aplicación'}.`, tono: 'ok' }, parrafo: null };
    case 'rechazado': return { titulo, linea: { icono: 'x', texto: `No diste el permiso: ${d.cliente || 'la aplicación'} no podrá usar GEOBAJA.` }, parrafo: null };
    default: return { titulo, linea: null, parrafo: null };
  }
}

/** La línea de regreso: el host que recibirá la decisión (el canon de MCP pide decirlo). */
export const vuelveA = (cliente) => `Volviendo a ${cliente || 'la aplicación'}…`;
